import classNames from 'classnames'
import React from 'react'

import { IconComponent } from '../types'

export type ButtonVariants = 'primary' | 'secondary' | 'outlined' | 'black' | 'tertiary'

export type ButtonSizes = 'small' | 'medium' | 'large'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariants
  size?: ButtonSizes
  label?: string
  block?: boolean
  prefixIcon?: IconComponent
  suffixIcon?: IconComponent
  className?: string
  loading?: boolean
  backgroundColor?: string
}

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  label,
  block,
  size = 'medium',
  prefixIcon: PrefixIcon,
  suffixIcon: SuffixIcon,
  className,
  loading = false,
  backgroundColor,
  ...buttonProps
}) => {
  return (
    <button
      {...buttonProps}
      className={classNames(
        className,
        'rounded-3xl cursor-pointer text-center outline-none inline-flex items-center justify-center gap-[.5em] leading-none disabled:bg-neutralBlack-400 disabled:pointer-events-none',
        {
          'hover:brightness-105': !buttonProps.disabled && !loading,
          'bg-neutralBlack-400': buttonProps.disabled || loading,
          'bg-[#1A1A1A] hover:bg-neutralBlack-800 text-white':
            variant === 'primary' || variant === 'black',
          'bg-secondary text-neutralBlack-700 border-[#2E2E2E] border-[1.5px] ':
            variant === 'secondary',
          'border-[1.5px]  bg-secondaryborder-neutralBlack-600 text-neutralBlack-600 hover:text-black hover:border-black':
            variant === 'outlined',
          'bg-tertiary': variant === 'tertiary',
          'w-full': block,
          'min-h-12 px-8 text-[18px] ': size === 'large',
          'min-h-9 md:min-h-11 px-4 md:px-6 text-[0.9rem] md:text-[1.1rem] ': size === 'medium',
          'min-h-8 px-3 text-[12px] ': size === 'small'
        }
      )}
      disabled={loading || buttonProps.disabled}
      style={{
        boxShadow: '1px 2px 4px 0px #0000001A',
        backgroundColor: backgroundColor
      }}
    >
      {loading && (
        <svg
          className="animate-spin h-5 w-5 text-primary mr-2"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 50 50"
        >
          <circle
            className="opacity-25"
            cx="25"
            cy="25"
            r="20"
            stroke="white"
            strokeWidth="5"
            fill="none"
          ></circle>
          <circle
            className="opacity-75"
            cx="25"
            cy="25"
            r="20"
            stroke="currentColor"
            strokeWidth="5"
            strokeLinecap="round"
            fill="none"
            strokeDasharray="31.415, 31.415"
            strokeDashoffset="0"
          >
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="0 25 25"
              to="360 25 25"
              dur="3s"
              repeatCount="indefinite"
            />
          </circle>
        </svg>
      )}

      {PrefixIcon && !loading && <PrefixIcon className="size-[1.35em] -translate-y-px" />}

      {(children || label) && (
        <span className="whitespace-nowrap overflow-hidden text-ellipsis leading-5 flex gap-[.25em] items-center">
          {children || label}
        </span>
      )}

      {SuffixIcon && !loading && <SuffixIcon className="size-[1.35em] -translate-y-px" />}
    </button>
  )
}
